// EcosystemColumn — one method column on /admin/provider-status.
//
// Shape (modelled on Razorpay's Ecosystem Health view):
//
//   ┌───────────────────────┐
//   │ UPI                   │  ← column header
//   ├───────────────────────┤
//   │ ● All instruments are │  ← per-column status banner
//   │   functional          │     (green / amber / red)
//   │   No ongoing downtimes│
//   ├───────────────────────┤
//   │ VPA handle            │  ← group label
//   │ ┌─────┐ apl       ●   │  ← instrument row card
//   │ ┌─────┐ okicici   ●   │
//   │ ...                   │
//   ├───────────────────────┤
//   │ PSP apps              │  ← next group label
//   │ ...                   │
//   └───────────────────────┘
//
// Affected rows render with a soft red tint and a red dot; healthy rows
// stay on the card surface with a green dot.

import { useState } from "react";

import { I } from "@/components/Icons";
import { InstrumentLogo } from "./InstrumentLogo";
import type { RazorpayDowntime } from "@/api/razorpayDowntimes";
import {
  type MethodColumn,
  findInstrumentName,
} from "@/data/razorpayEcosystem";
import { formatDateTime } from "@/utils/format";

interface Props {
  column: MethodColumn;
  /** Downtimes that match this column (caller does the per-column filter). */
  downtimes: RazorpayDowntime[];
}

export function EcosystemColumn({ column, downtimes }: Props) {
  // Build a Set of affected instrument ids for O(1) row-level lookup.
  // Downtimes whose code doesn't appear in any known group fall into
  // `unknownDowntimes` and render as a separate "Other" group at the bottom
  // so we don't silently drop new Razorpay codes.
  const affectedIds = new Set<string>();
  const unknown: RazorpayDowntime[] = [];
  for (const d of downtimes) {
    const id = column.match(d);
    if (!id) continue;
    const inManifest = column.groups.some((g) =>
      g.items.some((i) => i.id === id),
    );
    if (inManifest) {
      affectedIds.add(id);
    } else {
      unknown.push(d);
    }
  }

  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
      }}
    >
      <h3
        style={{
          fontSize: 14,
          fontWeight: 600,
          margin: 0,
          paddingBottom: 6,
          borderBottom: "2px solid var(--accent, #0e8a3e)",
          width: "fit-content",
        }}
      >
        {column.label}
      </h3>

      <ColumnStatusBanner
        downtimes={downtimes}
        column={column}
      />

      {column.groups.map((group) => (
        <div key={group.label}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: "var(--ink-2)",
              marginBottom: 6,
              marginTop: 4,
            }}
          >
            {group.label}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {group.items.map((it) => (
              <InstrumentRow
                key={it.id}
                name={it.name}
                monogram={it.monogram}
                color={it.color}
                affected={affectedIds.has(it.id)}
              />
            ))}
          </div>
        </div>
      ))}

      {unknown.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.02em",
              color: "var(--ink-2)",
              marginBottom: 6,
            }}
          >
            Other
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {unknown.map((d) => {
              const id = column.match(d) ?? "?";
              return (
                <InstrumentRow
                  key={d.id}
                  name={findInstrumentName(column, id)}
                  monogram={id.slice(0, 2).toUpperCase()}
                  color="#888888"
                  affected
                />
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

// ── Status banner per column ──────────────────────────────────────────────

function ColumnStatusBanner({
  downtimes,
  column,
}: {
  downtimes: RazorpayDowntime[];
  column: MethodColumn;
}) {
  const [expanded, setExpanded] = useState(false);

  if (downtimes.length === 0) {
    return (
      <BannerShell tone="ok">
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            <Dot tone="ok" /> All instruments are functional
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
            No ongoing downtimes
          </div>
        </div>
      </BannerShell>
    );
  }

  const first = downtimes[0];
  const rest = downtimes.slice(1);
  const tone: BannerTone =
    first.severity === "high" ? "warn" : first.severity === "medium" ? "amber" : "ok";

  // Severity headline — match Razorpay's wording ("High Severity Downtime").
  const headline =
    first.severity === "high"
      ? "High Severity Downtime"
      : first.severity === "medium"
        ? "Medium severity downtime"
        : "Minor disruption";

  return (
    <BannerShell tone={tone}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>
          <Dot tone={tone} /> {headline}
        </div>
        <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
          {downtimeSummary(first, column)}
        </div>
        {rest.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              marginTop: 6,
              fontSize: 11,
              color: "var(--accent, #0e8a3e)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {expanded
              ? `Hide other downtimes`
              : `+${rest.length} other${rest.length === 1 ? "" : "s"} · Show other downtimes`}
            <I.ChevronDown
              size={10}
              style={{ transform: expanded ? "rotate(180deg)" : undefined }}
            />
          </button>
        )}
        {expanded && rest.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "8px 0 0 0",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            {rest.map((d) => (
              <li key={d.id} className="dim" style={{ fontSize: 11 }}>
                · {downtimeSummary(d, column)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </BannerShell>
  );
}

function downtimeSummary(d: RazorpayDowntime, column: MethodColumn): string {
  const id = column.match(d) ?? "?";
  const name = findInstrumentName(column, id);
  const began = formatDateTime(new Date(d.begin * 1000).toISOString());
  return `${name} (started at ${began})`;
}

// ── Atom: status dot + banner shell ───────────────────────────────────────

type BannerTone = "ok" | "amber" | "warn";

function BannerShell({
  tone,
  children,
}: {
  tone: BannerTone;
  children: React.ReactNode;
}) {
  const bg =
    tone === "warn"
      ? "var(--warn-soft, #fef3f2)"
      : tone === "amber"
        ? "var(--amber-soft, #fef9e7)"
        : "var(--ok-soft, #f0fdf4)";
  const border =
    tone === "warn"
      ? "var(--warn, #b42318)"
      : tone === "amber"
        ? "var(--amber, #b88a00)"
        : "var(--ok, #1a7f37)";
  return (
    <div
      style={{
        padding: "10px 12px",
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 6,
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
      }}
    >
      {children}
    </div>
  );
}

function Dot({ tone }: { tone: BannerTone }) {
  const color =
    tone === "warn"
      ? "var(--warn, #b42318)"
      : tone === "amber"
        ? "var(--amber, #b88a00)"
        : "var(--ok, #1a7f37)";
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        marginRight: 8,
        verticalAlign: "middle",
      }}
    />
  );
}

// ── Instrument row card ───────────────────────────────────────────────────

function InstrumentRow({
  name,
  monogram,
  color,
  affected,
}: {
  name: string;
  monogram: string;
  color: string;
  affected: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: affected
          ? "var(--warn-soft, #fef3f2)"
          : "var(--surface, #ffffff)",
        border: `1px solid ${affected ? "var(--warn, #b42318)" : "var(--hair, rgba(0,0,0,0.08))"}`,
        borderRadius: 6,
        minHeight: 40,
      }}
    >
      <InstrumentLogo monogram={monogram} color={color} />
      <span style={{ fontSize: 13, flex: 1, minWidth: 0, color: "var(--ink-1)" }}>
        {name}
      </span>
      <span
        title={affected ? "Ongoing downtime" : "Operational"}
        aria-label={affected ? "Ongoing downtime" : "Operational"}
        style={{
          display: "inline-block",
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: affected ? "var(--warn, #b42318)" : "var(--ok, #1a7f37)",
          flexShrink: 0,
        }}
      />
    </div>
  );
}
